import { Card, CardBody, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react';
import NextLink from 'next/link';

import { ComparisonPageLayout } from '../../components/comparisons/ComparisonPageLayout';
import { Section } from '../../components/layout/Storyblok/Section/Section';
import wysiwygStyles from '../../components/layout/Storyblok/Wysiwyg/Wysiwyg.module.scss';
import { formatIsoDate, LastVerifiedBadge } from '../../components/pseo/LastVerifiedBadge';
import { CTASection } from '../../components/shared/CTASection';
import { FAQStructuredData } from '../../components/shared/FAQStructuredData';
import { ItemListStructuredData } from '../../components/shared/ItemListStructuredData';
import { Icon } from '../../components/various/Icon';
import { competitorComparisonPath, getPublishedCompetitorComparisons } from '../../data/competitor-comparisons';
import {
  COMPETITOR_PRICING,
  COMPETITOR_PRICING_MAX_AGE_DAYS,
  type CompetitorId,
  type CompetitorPricing,
  competitorPricingPath,
  describeStartingPrice,
  formatPricingAmount,
  getCompetitorPricing,
  getMonthlyCostForSeats,
  hasPublishedPricingPage,
} from '../../data/competitor-pricing';
import {
  BILLING_PERIOD,
  EXPLORATION_MODE,
  getKreatliMonthlyCost,
  getKreatliPlan,
  TRIAL_DAYS,
} from '../../data/kreatli-plans';
import { warnStaleEntries } from '../../data/pseo/staleness';
import type { SourceRef } from '../../data/pseo/types';
import { isPublishable } from '../../data/pseo/types';
import { useSession } from '../../hooks/useSession';
import { SectionStoryblok } from '../../typings/storyblok';
import { getHeadingId } from '../../utils/storyblok';

const DOCUMENT_TITLE = 'Best Video Review Software (2026)';
const META_DESCRIPTION =
  'Compare 8 video review tools: Kreatli, Frame.io, Filestage, Ziflow, Wipster, Dropbox Replay, PageProof and GoVisually, with pricing for 5 seats.';
const PUBLISH_DATE = '2026-10-22 00:00';
const READ_TIME = '12';
const CANONICAL_PATH = '/comparisons/best-video-review-software';
const COVER_SRC =
  'https://a.storyblok.com/f/287637539865613/2250x750/8d83a78aaf/best-video-review-platforms-for-creative-teams-in-2026.png';
const TEAM_SIZE = 5;

const HERO_BLOK: SectionStoryblok = {
  _uid: 'static-best-video-review-software-hero',
  component: 'section',
  title: 'Best Video Review Software (2026)',
  titleTag: 'h1',
  text: 'Eight video review tools compared on file types, guest review, approvals, editing-software integrations and what a 5-person team pays each month.',
  orientation: '',
  displaySocials: false,
  imageObjectFit: 'cover',
  image: {
    _uid: 'static-best-video-review-software-cover',
    id: null,
    alt: 'Video review software comparison',
    name: '',
    focus: '',
    source: '',
    title: 'Best video review software 2026',
    filename: COVER_SRC,
    copyright: '',
    fieldtype: 'asset',
  },
};

const TOC_SECTIONS = [
  'Quick picks',
  'How we compared video review software',
  'Pricing for a 5-person team',
  'The 8 video review tools',
  'When Kreatli is not the right choice',
  'Sources',
  'FAQ: video review software',
] as const;

const creator = getKreatliPlan('creator');
const team = getKreatliPlan('team');
const kreatliFiveSeats = getKreatliMonthlyCost(TEAM_SIZE);

interface ToolProfile {
  name: string;
  /** Registry entry for competitors; Kreatli prices come from `kreatli-plans.ts`. */
  pricingId?: CompetitorId;
  homepageUrl: string;
  bestFor: string;
  strengths: string[];
  limits: string[];
  /** Official sources for the non-pricing facts. */
  sources: SourceRef[];
}

/** Facts come only from the Oct 21, 2026 fact sheet (official vendor pages). Kreatli: verified features only. */
const TOOLS: ToolProfile[] = [
  {
    name: 'Kreatli',
    homepageUrl: 'https://kreatli.com/platform/review-approval',
    bestFor: 'Creative teams that review video, images, PDFs and audio with clients and run tasks in the same place.',
    strengths: [
      'Timecoded and time-range comments, drawn markup and PDF page comments.',
      'Guest review without login (name only), on every plan.',
      'Custom approval statuses that guests can see on the shared link.',
      'Version stacks and side-by-side version compare.',
      'Tasks, deliverables and chat alongside the review.',
    ],
    limits: [
      'No comment export (CSV, PDF or editing-software markers).',
      'No panels or plugins for Premiere Pro, Final Cut Pro or DaVinci Resolve.',
      'No website proofing and no frame-by-frame stepping.',
      `Listed plans go up to ${team.memberLimit} members; larger teams are on Enterprise (custom pricing).`,
    ],
    sources: [{ label: 'Kreatli pricing', url: 'https://kreatli.com/pricing' }],
  },
  {
    name: 'Frame.io',
    pricingId: 'frame-io',
    homepageUrl: 'https://www.frame.io',
    bestFor: 'Video editors in Adobe tools who want review inside Premiere Pro and After Effects.',
    strengths: [
      'Premiere Pro panel (V4, 25.6 and later) plus After Effects, Lightroom, LumaFusion and Baselight integrations.',
      'Reviews video, images, documents and Office files.',
      'Share reviewers can comment without an account.',
      'Comment export to CSV or plain text for editing software, and Print Comments to PDF.',
    ],
    limits: [
      'Priced per member; more than 15 members means Enterprise with custom pricing.',
      'Free plan: 2 members, 2GB storage and 2 projects.',
      'The DaVinci Resolve integration is legacy and Studio-only.',
    ],
    sources: [
      { label: 'Frame.io integrations', url: 'https://frame.io/integrations' },
      {
        label: 'Frame.io help: comment exporting',
        url: 'https://help.frame.io/en/articles/9105309-comment-printing-and-comment-exporting',
      },
    ],
  },
  {
    name: 'Filestage',
    pricingId: 'filestage',
    homepageUrl: 'https://filestage.io',
    bestFor: 'Marketing teams and agencies proofing many formats, including live websites, on a flat price.',
    strengths: [
      'Reviews video, designs, PDFs, documents, live websites and HTML.',
      'Approve or request changes, with due dates and reminders.',
      'Unlimited reviewers, no account needed.',
      'Integrations with Premiere Pro, Photoshop and InDesign.',
    ],
    limits: [
      'Priced in EUR.',
      '10 seats on every plan; extra seat packs are not priced on the pricing page.',
      'Free plan: 1 active project and 5 new files per month.',
    ],
    sources: [{ label: 'Filestage', url: 'https://filestage.io/' }],
  },
  {
    name: 'Ziflow',
    pricingId: 'ziflow',
    homepageUrl: 'https://www.ziflow.com',
    bestFor: 'Larger review teams that want many seats included in one flat annual price.',
    strengths: [
      'Standard includes 15 seats and Pro includes 20 seats for one flat price.',
      'Unlimited reviewers on every plan.',
      'A free-forever plan for 2 seats.',
    ],
    limits: [
      'Listed prices are billed annually.',
      'Secure Guest Reviewing is Enterprise only.',
      'Free plan: 1 workflow stage and 60-day history.',
    ],
    sources: [{ label: 'Ziflow pricing', url: 'https://www.ziflow.com/pricing' }],
  },
  {
    name: 'Wipster',
    pricingId: 'wipster',
    homepageUrl: 'https://wipster.io',
    bestFor: 'Video teams that want editing-software panels and multi-step approvals.',
    strengths: [
      'Panels for Premiere Pro, Final Cut Pro and After Effects.',
      'Multi-step approvals with an audit trail and one-click sign-off.',
      'Reviews video, images, PDF and audio; free, unlimited guest reviewers.',
    ],
    limits: ['No free plan on its pricing page.', 'SSO is Enterprise only.', 'Team storage starts at 250GB.'],
    sources: [{ label: 'Wipster', url: 'https://wipster.io/' }],
  },
  {
    name: 'Dropbox Replay',
    pricingId: 'dropbox-replay',
    homepageUrl: 'https://www.dropbox.com/replay',
    bestFor: 'Teams already paying for Dropbox Plus or higher that want review without another app.',
    strengths: [
      'Reviews video, images, audio, PSD and PDF.',
      'Integrations with Premiere Pro, Final Cut Pro, DaVinci Resolve, After Effects and Pro Tools.',
      'Reviewers can comment without a Dropbox account.',
    ],
    limits: [
      'An add-on: you also pay for the Dropbox plan, and it is not available on Family plans.',
      'Without the add-on you get 4 or 10 file uploads, depending on the plan.',
    ],
    sources: [
      { label: 'Dropbox Replay', url: 'https://www.dropbox.com/replay' },
      { label: 'Dropbox help: Replay', url: 'https://help.dropbox.com/installs/dropbox-replay' },
    ],
  },
  {
    name: 'PageProof',
    pricingId: 'pageproof',
    homepageUrl: 'https://pageproof.com',
    bestFor: 'Teams that want unlimited users, storage and reviewers for one flat monthly price.',
    strengths: ['Unlimited users, storage and reviewers on every plan.', 'SSO from the Team Plus plan.'],
    limits: [
      'No free plan; trial on request.',
      [
        `The cheapest plan is `,
        `${formatPricingAmount(getCompetitorPricing('pageproof').plans[0].priceMonthly ?? 0, 'USD')} per month, `,
        `whatever the team size.`,
      ].join(''),
    ],
    sources: [{ label: 'PageProof pricing', url: 'https://pageproof.com/pricing' }],
  },
  {
    name: 'GoVisually',
    pricingId: 'govisually',
    homepageUrl: 'https://govisually.com',
    bestFor: 'Teams reviewing design files and artwork, including CPG brands that need its compliance suite.',
    strengths: ['Pro includes unlimited reviewers.', 'Trial without a credit card.'],
    limits: [
      'The Lite plan has no video review, 15 active projects and up to 15 external reviewers.',
      'Pro bills a minimum of 3 users; no free plan.',
    ],
    sources: [{ label: 'GoVisually pricing', url: 'https://govisually.com/pricing/' }],
  },
];

const pricingFor = (tool: ToolProfile): CompetitorPricing | null =>
  tool.pricingId ? getCompetitorPricing(tool.pricingId) : null;

/** Registry entry when the tool has a published `/comparisons/<vendor>-pricing` page. */
const pricingPageFor = (tool: ToolProfile): CompetitorPricing | null => {
  const entry = pricingFor(tool);
  return entry && hasPublishedPricingPage(entry) ? entry : null;
};

const HEAD_TO_HEAD_LINKS = getPublishedCompetitorComparisons().map((comparison) => ({
  href: competitorComparisonPath(comparison),
  label: `${getCompetitorPricing(comparison.a).vendor} vs ${getCompetitorPricing(comparison.b).vendor}`,
}));

/** Draft entries never show their (unverified) numbers; show the regional price or send readers to the vendor. */
function draftPriceNote(entry: CompetitorPricing): string {
  const regional = entry.regionalPricing;
  if (!regional) return 'Check the vendor for current pricing';
  const annual =
    regional.priceAnnualMonthly !== undefined
      ? ` (${formatPricingAmount(regional.priceAnnualMonthly, regional.currency)} billed annually)`
      : '';
  return [
    `${regional.label}: ${formatPricingAmount(regional.priceMonthly, regional.currency)} per user/month${annual}. `,
    `Check Dropbox for pricing in your region`,
  ].join('');
}

interface PricingRow {
  name: string;
  model: string;
  startingPrice: string;
  freePlan: string;
  fiveSeats: string;
  note: string;
}

const MODEL_LABEL: Record<CompetitorPricing['basis'], string> = {
  'per-seat': 'Per seat',
  flat: 'Flat price',
  'add-on': 'Per-user add-on',
};

const PRICING_ROWS: PricingRow[] = TOOLS.map((tool) => {
  const entry = pricingFor(tool);
  if (!entry) {
    return {
      name: tool.name,
      model: 'Per seat',
      startingPrice: `$${creator.priceMonthly} per seat/month (${creator.name})`,
      freePlan: `Yes (${EXPLORATION_MODE.name}: ${EXPLORATION_MODE.label})`,
      fiveSeats:
        kreatliFiveSeats.total === null
          ? `Custom (${kreatliFiveSeats.plan.name})`
          : `$${kreatliFiveSeats.total} (${kreatliFiveSeats.plan.name})`,
      note: [
        `${kreatliFiveSeats.plan.name}: ${TEAM_SIZE} × $${kreatliFiveSeats.plan.priceMonthly} per seat. Billed `,
        `${BILLING_PERIOD}.`,
      ].join(''),
    };
  }
  if (!isPublishable(entry)) {
    return {
      name: tool.name,
      model: MODEL_LABEL[entry.basis],
      startingPrice: 'Check vendor',
      freePlan: entry.freePlan ? 'Limited' : 'No',
      fiveSeats: 'Check vendor',
      note: draftPriceNote(entry),
    };
  }
  const cost = getMonthlyCostForSeats(entry, TEAM_SIZE, { requireVideo: true });
  return {
    name: tool.name,
    model: MODEL_LABEL[entry.basis],
    startingPrice: describeStartingPrice(entry),
    freePlan: entry.freePlan ? 'Yes' : 'No',
    fiveSeats:
      cost.total === null ? 'Not listed' : `${formatPricingAmount(cost.total, cost.currency)} (${cost.planName})`,
    note: cost.note,
  };
});

const PUBLISHED_ENTRIES = TOOLS.map(pricingFor).filter(
  (entry): entry is CompetitorPricing => entry !== null && isPublishable(entry),
);
const VERIFIED_ON = PUBLISHED_ENTRIES.map((entry) => entry.lastVerified).sort()[0];

const ALL_SOURCES: SourceRef[] = Array.from(
  new Map(
    TOOLS.flatMap((tool) => [...(pricingFor(tool)?.sourceUrls ?? []), ...tool.sources]).map((source) => [
      source.url,
      source,
    ]),
  ).values(),
);

/** "Frame.io $75 (Pro), Wipster $125 (Team), …" sorted by USD cost; non-USD listed after. */
function fiveSeatSummary(): string {
  const rows = PUBLISHED_ENTRIES.map((entry) => ({
    entry,
    cost: getMonthlyCostForSeats(entry, TEAM_SIZE, { requireVideo: true }),
  })).filter((row) => row.cost.total !== null);
  const usd = [
    ...rows
      .filter((row) => row.cost.currency === 'USD')
      .map((row) => ({ name: row.entry.vendor, total: row.cost.total as number, plan: row.cost.planName })),
    ...(kreatliFiveSeats.total === null
      ? []
      : [{ name: 'Kreatli', total: kreatliFiveSeats.total, plan: kreatliFiveSeats.plan.name }]),
  ].sort((a, b) => a.total - b.total);
  const other = rows.filter((row) => row.cost.currency !== 'USD');
  return [
    ...usd.map((row) => `${row.name} $${row.total} (${row.plan})`),
    ...other.map((row) =>
      [
        `${row.entry.vendor} ${formatPricingAmount(row.cost.total as number, row.cost.currency)} `,
        `(${row.cost.planName}, billed in ${row.cost.currency})`,
      ].join(''),
    ),
  ].join(', ');
}

const FAQS = [
  {
    question: 'What is the best video review software?',
    answer:
      'It depends on your workflow. Frame.io suits editors who want review inside Adobe tools, Filestage suits teams proofing many formats including websites, Wipster suits teams that want editing-software panels and multi-step approvals, and Dropbox Replay suits teams already on Dropbox. Kreatli suits creative teams that review video, images, PDFs and audio with clients and want tasks and deliverables in the same project.',
  },
  {
    question: 'Which video review software is cheapest for a team of 5?',
    answer: [
      `Based on each vendor's listed prices (checked ${formatIsoDate(VERIFIED_ON)}), the monthly cost for `,
      `${TEAM_SIZE} seats on a plan that reviews video is: ${fiveSeatSummary()}. Ziflow's price is billed annually. `,
      `Dropbox Replay is left out because its US price wasn't confirmed on its US pricing page.`,
    ].join(''),
  },
  {
    question: 'Can clients review videos without creating an account?',
    answer:
      'Yes, in most of these tools. Kreatli guests comment from a link with just their name, Frame.io share reviewers comment without an account, Filestage and Wipster offer unlimited reviewers without accounts, and Dropbox Replay reviewers don’t need a Dropbox account. Ziflow lists unlimited reviewers, but its Secure Guest Reviewing feature is Enterprise only.',
  },
  {
    question: 'Which video review tools integrate with Premiere Pro?',
    answer:
      'Frame.io (V4 panel for Premiere Pro 25.6 and later), Filestage, Wipster and Dropbox Replay all list Premiere Pro integrations. Kreatli has no editing-software panels; you upload exports and share a review link.',
  },
  {
    question: 'Does Kreatli have a free plan?',
    answer: [
      `Kreatli's ${EXPLORATION_MODE.name} mode is free: ${EXPLORATION_MODE.label}. Paid plans start with a `,
      `${TRIAL_DAYS}-day trial: ${creator.name} is $${creator.priceMonthly} per user per month for up to `,
      `${creator.memberLimit} members, and ${team.name} is $${team.priceMonthly} per user per month for up to `,
      `${team.memberLimit} members.`,
    ].join(''),
  },
  {
    question: 'How were these tools compared?',
    answer: [
      `We compared the file types each tool reviews, guest review, approvals, editing-software integrations and `,
      `pricing, using each vendor's official pages checked on ${formatIsoDate(VERIFIED_ON)}. Kreatli is our `,
      `product, so we list what it can't do as well as what it can. Prices change, so confirm on the vendor's site `,
      `before you buy.`,
    ].join(''),
  },
];

const ITEM_LIST = TOOLS.map((tool) => ({ name: tool.name, url: tool.homepageUrl }));

export default function BestVideoReviewSoftwarePage() {
  useSession();

  const tocLinks = TOC_SECTIONS.map((label) => ({ label, url: `#${getHeadingId(label)}` }));

  return (
    <>
      <FAQStructuredData faqs={FAQS} />
      <ItemListStructuredData name={DOCUMENT_TITLE} url={`https://kreatli.com${CANONICAL_PATH}`} items={ITEM_LIST} />
      <ComparisonPageLayout
        documentTitle={DOCUMENT_TITLE}
        metaDescription={META_DESCRIPTION}
        canonicalPath={CANONICAL_PATH}
        ogImageUrl={COVER_SRC}
        ogImageAlt="Video review software comparison"
        publishDate={PUBLISH_DATE}
        readTimeMinutes={READ_TIME}
        breadcrumbLabel="Best video review software"
        tocLinks={tocLinks}
      >
        <Section blok={HERO_BLOK} />

        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-transparent p-6 backdrop-blur-lg sm:p-8">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex-shrink-0 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 p-2">
              <Icon icon="checkCircle" size={18} className="text-primary" />
            </div>
            <p className="font-sans text-base font-bold">Article summary</p>
          </div>
          <p className="pl-1 text-sm leading-relaxed text-foreground-600 sm:text-base">
            Video review software lets your team and clients leave timecoded comments on a cut and mark which version is
            approved. We compared eight tools on what they review, how guests get in, how approvals work and what 5
            seats cost, using pricing from each vendor&apos;s own site. Kreatli is our product; the fairness section
            says when to pick something else.
          </p>
        </div>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[0])}>{TOC_SECTIONS[0]}</h2>
          <ul>
            {TOOLS.map((tool) => (
              <li key={tool.name}>
                <strong>{tool.name}:</strong> {tool.bestFor}
              </li>
            ))}
          </ul>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[1])}>{TOC_SECTIONS[1]}</h2>
          <p>Each tool is described with the same five criteria, using the vendor&apos;s own pages:</p>
          <ol>
            <li>
              <strong>File types:</strong> video only, or also images, PDFs, audio and documents.
            </li>
            <li>
              <strong>Guest review:</strong> whether clients can comment without an account or a paid seat.
            </li>
            <li>
              <strong>Approvals:</strong> statuses, sign-off steps, due dates or reminders.
            </li>
            <li>
              <strong>Editing-software integrations:</strong> panels for Premiere Pro, Final Cut Pro, After Effects or
              DaVinci Resolve.
            </li>
            <li>
              <strong>Pricing:</strong> per seat or flat, the free plan, and the monthly cost for a {TEAM_SIZE}-person
              team on the cheapest listed plan that reviews video.
            </li>
          </ol>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[2])}>{TOC_SECTIONS[2]}</h2>
          <p>
            Costs are list prices per month before tax, for {TEAM_SIZE} seats on the cheapest listed plan that reviews
            video. Free plans are left out because of their project and file limits.
          </p>
        </div>

        <Card className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
          <CardBody className="overflow-x-auto p-2 sm:p-4">
            <Table
              aria-label={`Video review software pricing for ${TEAM_SIZE} seats`}
              removeWrapper
              className="min-w-[760px]"
            >
              <TableHeader>
                <TableColumn>Tool</TableColumn>
                <TableColumn>Pricing model</TableColumn>
                <TableColumn>Starting price</TableColumn>
                <TableColumn>Free plan</TableColumn>
                <TableColumn>{`${TEAM_SIZE} seats / month`}</TableColumn>
                <TableColumn>How it&apos;s calculated</TableColumn>
              </TableHeader>
              <TableBody>
                {PRICING_ROWS.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell className="font-semibold text-foreground-700">{row.name}</TableCell>
                    <TableCell>{row.model}</TableCell>
                    <TableCell>{row.startingPrice}</TableCell>
                    <TableCell>{row.freePlan}</TableCell>
                    <TableCell className="font-semibold">{row.fiveSeats}</TableCell>
                    <TableCell className="text-sm text-foreground-500">{row.note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <LastVerifiedBadge
          lastVerified={VERIFIED_ON}
          sources={PUBLISHED_ENTRIES.flatMap((entry) => entry.sourceUrls.slice(0, 1))}
          notes={[
            'Filestage prices are in EUR and are not converted.',
            'Dropbox Replay is shown with its EU price only; its USD price could not be confirmed on the US page.',
          ]}
        />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[3])}>{TOC_SECTIONS[3]}</h2>
          <p>Listed in the same order as the quick picks. Kreatli is first because it&apos;s our product.</p>
        </div>

        <div className="flex flex-col gap-6">
          {TOOLS.map((tool, index) => {
            const row = PRICING_ROWS[index];
            return (
              <Card key={tool.name} className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
                <CardBody className="flex flex-col gap-4 p-6 sm:p-8">
                  <div className="flex flex-col gap-1">
                    <h3 className="font-sans text-xl font-bold sm:text-2xl">
                      {index + 1}. {tool.name}
                    </h3>
                    <p className="text-base text-foreground-600">
                      <span className="font-semibold text-foreground-700">Best for:</span> {tool.bestFor}
                    </p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="mb-2 font-semibold text-foreground-700">Strengths</p>
                      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-foreground-600 sm:text-base">
                        {tool.strengths.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="mb-2 font-semibold text-foreground-700">Limits</p>
                      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-foreground-600 sm:text-base">
                        {tool.limits.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 sm:text-base">
                    <span className="font-semibold text-foreground-700">Pricing:</span>{' '}
                    {row.startingPrice === 'Check vendor' ? row.note : `From ${row.startingPrice}.`}{' '}
                    {row.fiveSeats !== 'Check vendor' ? `${TEAM_SIZE} seats: ${row.fiveSeats}.` : null}
                    {pricingPageFor(tool) ? (
                      <>
                        {' '}
                        <NextLink
                          href={competitorPricingPath(pricingPageFor(tool) as CompetitorPricing)}
                          className="text-primary underline underline-offset-2"
                        >
                          {`Full ${tool.name} pricing breakdown`}
                        </NextLink>
                      </>
                    ) : null}
                  </p>
                  <p className="text-sm text-foreground-500">
                    Sources:{' '}
                    {[...(pricingFor(tool)?.sourceUrls ?? []), ...tool.sources].map((source, sourceIndex) => (
                      <span key={source.url}>
                        {sourceIndex > 0 ? ', ' : null}
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline underline-offset-2"
                        >
                          {source.label}
                        </a>
                      </span>
                    ))}
                  </p>
                </CardBody>
              </Card>
            );
          })}
        </div>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[4])}>{TOC_SECTIONS[4]}</h2>
          <p>Pick another tool when one of these matters more than mixed-media client review:</p>
          <ul>
            <li>
              <strong>You want review inside your editing software.</strong> Kreatli has no Premiere Pro, Final Cut Pro
              or DaVinci Resolve panels. Frame.io, Wipster and Dropbox Replay do.
            </li>
            <li>
              <strong>You need to export comments.</strong> Kreatli doesn&apos;t export comments. Frame.io exports CSV
              and plain text for editing software and prints comments to PDF.
            </li>
            <li>
              <strong>You proof live websites or HTML.</strong> Kreatli has no website proofing; Filestage does.
            </li>
            <li>
              <strong>You need frame-by-frame stepping.</strong> Kreatli comments are timecoded, but there is no frame
              stepping.
            </li>
            <li>
              <strong>Your team is larger than {team.memberLimit} and you want a listed price.</strong> Kreatli above{' '}
              {team.memberLimit} members is Enterprise with custom pricing. PageProof has unlimited users on a flat
              price, and Wipster Team has no user limit.
            </li>
            <li>
              <strong>Everything already lives in Dropbox.</strong> If you&apos;re on Dropbox Plus or higher, the Replay
              add-on keeps review next to your files.
            </li>
          </ul>
          <p>
            If none of these apply, compare Kreatli&apos;s{' '}
            <NextLink href="/platform/review-approval">video review software</NextLink> on one real client round.
          </p>
          <p>Head-to-head comparisons of the tools above:</p>
          <ul>
            {HEAD_TO_HEAD_LINKS.map((link) => (
              <li key={link.href}>
                <NextLink href={link.href}>{link.label}</NextLink>
              </li>
            ))}
          </ul>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[5])}>{TOC_SECTIONS[5]}</h2>
          <p>Official vendor pages used for this comparison:</p>
          <ul>
            {ALL_SOURCES.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noopener noreferrer">
                  {source.label}
                </a>
              </li>
            ))}
          </ul>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[6])}>{TOC_SECTIONS[6]}</h2>
        </div>

        <div className="flex flex-col gap-5">
          {FAQS.map((faq) => (
            <div key={faq.question} className="space-y-1.5">
              <h3 className="font-sans text-lg font-bold sm:text-xl">{faq.question}</h3>
              <p className="text-lg leading-relaxed text-foreground-500">{faq.answer}</p>
            </div>
          ))}
        </div>

        <CTASection
          title="Try Kreatli's video review software"
          description="Upload a cut, share a guest review link and collect timecoded comments. Explore free, no credit card."
          primaryButtonText="Start for Free"
          primaryButtonHref="/sign-up"
        />
      </ComparisonPageLayout>
    </>
  );
}

export function getStaticProps() {
  warnStaleEntries('competitor-pricing', COMPETITOR_PRICING, COMPETITOR_PRICING_MAX_AGE_DAYS);
  return { props: {} };
}
